import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext} from 'react-hook-form'
import {DetailsField} from '@/components/ui/details-atoms/DetailsAtoms'
import {ProtectedCategoryRadioButtons} from '@/features/patients/components/protected-category-radio-buttons/ProtectedCategoryRadioButtons'
import {InputSelectNeuromuscularDiagnosis} from '@/features/patients/components/input-select-neuromuscular-diagnosis/InputSelectNeuromuscularDiagnosis'
import {PatientMedicalInfoFormValidationSchema} from '@/features/patients/types'
import {LegalProtectionRadioButtons} from '@/features/patients/components/legal-protection-radio-buttons/LegalProtectionRadioButtons'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {patientTransportationModesToLabel} from '@/features/patients/utils'
import {LocalAuthorityAssistanceServiceRadioButtons} from '@/features/patients/components/local-authority-assistance-service-radio-buttons/LocalAuthorityAssistanceServiceRadioButtons'
import {PatientDisabilitiesRadioButtons} from '@/features/patients/components/patient-disabilities-radio-buttons/PatientDisabilitiesRadioButtons'
import {PatientAgeServiceType} from '@/components/commons/patient-age-service-type/PatientAgeServiceType'
import {Text} from '@/components/ui/text/Text'

export const PatientMedicalInfoFields = ({birthDate}: {birthDate: Date}) => {
    const {t} = useTranslation()

    // methods
    const form = useFormContext<PatientMedicalInfoFormValidationSchema>()

    return (
        <Grid columns={6} fullWidth gap={4}>
            <GridItem spanColumn={6}>
                <DetailsField>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('commons:service')}
                    </Text>
                    <Text size="textSm" weight="500">
                        <PatientAgeServiceType birthDate={birthDate} />
                    </Text>
                </DetailsField>
            </GridItem>

            <GridItem spanColumn={4}>
                <Controller
                    control={form.control}
                    name="neuromuscularDiagnosis"
                    render={({field, formState}) => (
                        <InputSelectNeuromuscularDiagnosis
                            label={t('patients:neuromuscular_neurological_diagnosis')}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('commons:select')}
                            isOptional
                            errorMessage={formState.errors.neuromuscularDiagnosis?.message}
                        />
                    )}
                />
            </GridItem>

            <GridItem spanColumn={4}>
                <Controller
                    control={form.control}
                    name="protectedCategory"
                    render={({field, formState}) => (
                        <ProtectedCategoryRadioButtons
                            name={field.name}
                            label={t('patients:protected_categories')}
                            value={field.value}
                            onChange={field.onChange}
                            error={formState.errors.protectedCategory}
                        />
                    )}
                />
            </GridItem>

            <GridItem spanColumn={4}>
                <Controller
                    control={form.control}
                    name="legalProtection"
                    render={({field, formState}) => (
                        <LegalProtectionRadioButtons
                            name={field.name}
                            label={t('patients:legal_protection')}
                            value={field.value}
                            onChange={field.onChange}
                            error={formState.errors.legalProtection}
                        />
                    )}
                />
            </GridItem>

            <GridItem spanColumn={4}>
                <Controller
                    control={form.control}
                    name="transportationMode"
                    render={({field, fieldState}) => (
                        <InputSelect
                            label={t('patients:transportation_modes')}
                            value={field.value ?? undefined}
                            optionsMap={patientTransportationModesToLabel}
                            errorMessage={fieldState.error?.message}
                            onChange={field.onChange}
                        />
                    )}
                />
            </GridItem>

            <GridItem spanColumn={4}>
                <Controller
                    control={form.control}
                    name="localAuthorityAssistanceService"
                    render={({field, formState}) => (
                        <LocalAuthorityAssistanceServiceRadioButtons
                            name={field.name}
                            label={t('patients:local_authority_assistance_service')}
                            value={field.value}
                            onChange={field.onChange}
                            error={formState.errors.localAuthorityAssistanceService}
                        />
                    )}
                />
            </GridItem>

            <GridItem spanColumn={6}>
                <Controller
                    control={form.control}
                    name="disability"
                    render={({field, formState}) => (
                        <PatientDisabilitiesRadioButtons
                            name={field.name}
                            label={t('patients:disability')}
                            value={field.value}
                            onChange={field.onChange}
                            error={formState.errors.disability}
                        />
                    )}
                />
            </GridItem>
        </Grid>
    )
}
